import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../config/api";
import useSeo from "../../hooks/useSeo";
import { buildWomanSeo, getStaticSeoForPath } from "../../utils/seo";
import { getDefaultWomanImageUrl, getWomanLargeImageUrl } from "../../utils/imageUrl";
import { getInitialData } from "../../utils/initialData";
import { getIdFromSlug } from "../../utils/slug";
import { useTranslation } from "../../i18n/LanguageProvider";
import ResponsiveAdSection from "../../Components/ResponsiveAdSection/ResponsiveAdSection";
import { AD_SLOTS } from "../../config/adSlots";
import Minigame from "../Home/Components/Minigame/Minigame";
import RandomWomenSection from "./Components/RandomWomenSection";
import RelatedWomenSidebar from "./Components/RelatedWomenSidebar";
import WomanNotFound from "./Components/WomanNotFound";
import WomanProfileCard from "./Components/WomanProfileCard";
import "./WomanPage.sass";

const RELATED_LIMIT = 4;
const RANDOM_LIMIT = 8;

// Profile the server already rendered for this URL (avoids a second request and a "loading" flash).
function getInitialProfile(id) {
    const initial = getInitialData("profile");

    return initial?.woman && String(initial.woman.id) === String(id) ? initial : null;
}

function WomanPage() {
    const { slug } = useParams();
    const { language, t } = useTranslation();
    // Extract numeric ID from the end of the slug (e.g. "maria-stefan_42" → "42")
    const id = getIdFromSlug(slug) || null;
    const [woman, setWoman] = useState(() => getInitialProfile(id)?.woman || null);
    const [loading, setLoading] = useState(() => !getInitialProfile(id));
    const [notFound, setNotFound] = useState(false);
    const [relatedWomen, setRelatedWomen] = useState(() => getInitialProfile(id)?.relatedWomen || []);
    const [randomWomen, setRandomWomen] = useState(() => getInitialProfile(id)?.randomWomen || []);
    const serverRenderedIdRef = useRef(null);
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [randomLoading, setRandomLoading] = useState(false);
    const seo = useMemo(() => {
        if (woman) return buildWomanSeo(woman, language);
        if (notFound) {
            return {
                title: `${t("profile.notFoundSeoTitle")} | ${t("site.name")}`,
                description: t("profile.notFoundSeoDescription"),
                image: getDefaultWomanImageUrl(),
                type: "website",
                robots: "noindex, follow",
            };
        }

        return getStaticSeoForPath("/femeie/", language);
    }, [language, notFound, t, woman]);

    useSeo(seo);

    useEffect(() => {
        window.scrollTo(0, 0);
        setNotFound(false);

        const initialProfile = getInitialProfile(id);

        if (initialProfile) {
            serverRenderedIdRef.current = String(initialProfile.woman.id);
            setWoman(initialProfile.woman);
            setRelatedWomen(initialProfile.relatedWomen || []);
            setRandomWomen(initialProfile.randomWomen || []);
            setLoading(false);
            return;
        }

        serverRenderedIdRef.current = null;
        setLoading(true);
        setWoman(null);
        setRelatedWomen([]);
        setRandomWomen([]);

        if (!id) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        fetch(`${API.getWoman}?womanId=${encodeURIComponent(id)}&lang=${language}`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                if (!data) {
                    setNotFound(true);
                } else {
                    setWoman(data);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id, language]);

    useEffect(() => {
        if (!woman?.id) return;
        // Recommendations came with the server-rendered profile.
        if (serverRenderedIdRef.current === String(woman.id)) return;

        const relatedParams = new URLSearchParams({
            related: "true",
            category_id: woman.category_id || "",
            exclude_id: woman.id,
            limit: RELATED_LIMIT,
            lang: language,
        });
        const randomParams = new URLSearchParams({
            isDefault: "true",
            limit: RANDOM_LIMIT,
            exclude_ids: woman.id,
            lang: language,
        });
        let cancelled = false;

        setRelatedLoading(true);
        setRandomLoading(true);

        Promise.all([
            fetch(`${API.getWoman}?${relatedParams.toString()}`, { credentials: "include" }).then((res) => res.json()),
            fetch(`${API.getWoman}?${randomParams.toString()}`, { credentials: "include" }).then((res) => res.json()),
        ])
            .then(([relatedData, randomData]) => {
                if (cancelled) return;
                setRelatedWomen(relatedData?.data || []);
                setRandomWomen(randomData?.data || []);
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Error loading profile recommendations:", err);
                    setRelatedWomen([]);
                    setRandomWomen([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setRelatedLoading(false);
                    setRandomLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [language, woman?.category_id, woman?.id]);

    // Schema.org structured data
    useEffect(() => {
        if (!woman) return;
        // The server already printed complete structured data for the page it rendered.
        if (document.getElementById("schema-main")) return;

        const schema = {
            "@context": "https://schema.org",
            "@type": "Person",
            name: `${woman.name} ${woman.surname}`,
            ...(woman.description && { description: woman.description }),
            ...(woman.category_name && { jobTitle: woman.category_name }),
            ...(woman.place_name && {
                workLocation: {
                    "@type": "Place",
                    name: woman.place_name,
                },
            }),
            ...(woman.image && {
                image: getWomanLargeImageUrl(woman.image),
            }),
            url: window.location.href,
        };

        const existing = document.getElementById("schema-person");
        if (existing) existing.remove();

        const script = document.createElement("script");
        script.id = "schema-person";
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById("schema-person");
            if (el) el.remove();
        };
    }, [woman]);

    if (loading) {
        return (
            <div className="woman-page general-wrapper">
                <p className="woman-page__loading">{t("profile.loading")}</p>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="woman-page general-wrapper">
                <WomanNotFound />
            </div>
        );
    }

    return (
        <div className="woman-page general-wrapper">
            <div className="woman-page__main-grid">
                <div className="woman-page__primary">
                    <WomanProfileCard woman={woman} />
                </div>
                <RelatedWomenSidebar women={relatedWomen} loading={relatedLoading} />
            </div>

            <section className="woman-page__minigame" aria-labelledby="profile-minigame-title">
                <h2 id="profile-minigame-title" className="woman-page__block-title">
                    {t("profile.recognize")}
                </h2>
                <Minigame />
            </section>

            <ResponsiveAdSection
                adSlot={AD_SLOTS.displayInline}
                className="woman-page__ad woman-page__ad--after-minigame"
            />

            <RandomWomenSection women={randomWomen} loading={randomLoading} />
        </div>
    );
}

export default WomanPage;
